import React from "react";
import { Lock, Calendar, Clock, Globe, Heart, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CountdownDisplay } from "@/hooks/useExhibitionEventState";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

interface ExhibitionPreEventGateProps {
  eventName: string | null;
  eventDescription?: string | null;
  startsAt: string | null;
  countdown: CountdownDisplay | null;
  eventState: "locked" | "closed" | "no-event";
  displayTitle?: string | null;
  displayMessage?: string | null;
  onExplorePreview?: () => void;
}

const ExhibitionPreEventGate: React.FC<ExhibitionPreEventGateProps> = ({
  eventName,
  eventDescription,
  startsAt,
  countdown,
  eventState,
  displayTitle,
  displayMessage,
  onExplorePreview,
}) => {
  const TimeUnit = ({ value, label }: { value: number; label: string }) => (
    <div className="flex flex-col items-center">
      <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center shadow-lg">
        <span className="text-2xl sm:text-3xl font-bold text-white">
          {value.toString().padStart(2, "0")}
        </span>
        <div className="absolute inset-0 rounded-2xl bg-white/10 animate-pulse" />
      </div>
      <span className="text-xs text-muted-foreground mt-2 font-medium uppercase tracking-wide">
        {label}
      </span>
    </div>
  );

  const Separator = () => (
    <div className="flex flex-col items-center justify-center gap-1.5 -mt-6">
      <div className="w-2 h-2 rounded-full bg-pink-400" />
      <div className="w-2 h-2 rounded-full bg-pink-400" />
    </div>
  );

  // No event state
  if (eventState === "no-event") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
        <div className="relative mb-8">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-pink-500/20 to-rose-500/20 flex items-center justify-center">
            <Calendar className="w-12 h-12 text-pink-500" />
          </div>
          <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-primary" />
          </div>
        </div>

        <h2 className="text-2xl font-bold text-foreground mb-2">No Event Scheduled</h2>
        <p className="text-muted-foreground max-w-sm mb-6">
          Love Exhibition happens every Saturday from 12 PM - 11 PM EST. 
          Check back soon for the next event!
        </p>

        <div className="p-4 rounded-2xl bg-muted/50 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4" />
            <span>Every Saturday • 12:00 PM - 11:00 PM EST</span>
          </div>
        </div>
      </div>
    );
  }

  // Closed state
  if (eventState === "closed") {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
        <div className="relative mb-8">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-gray-500/20 to-slate-500/20 flex items-center justify-center">
            <Lock className="w-12 h-12 text-gray-500" />
          </div>
        </div>

        <h2 className="text-2xl font-bold text-foreground mb-2">Exhibition Closed</h2>
        <p className="text-muted-foreground max-w-sm mb-6">
          {eventName || "Love Exhibition"} has ended for today. 
          See you next Saturday!
        </p>

        <div className="p-4 rounded-2xl bg-muted/50 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Heart className="w-4 h-4 text-pink-500" />
            <span>Thanks for joining! Check your matches.</span>
          </div>
        </div>
      </div>
    );
  }

  // Locked state with countdown
  // Use display_title/display_message if available, otherwise fall back to event name/description
  const title = displayTitle || eventName || "Love Exhibition";
  const message = displayMessage || eventDescription;

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
      {/* Header */}
      <div className="mb-6">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-pink-500/10 to-rose-500/10 border border-pink-500/20 mb-4">
          <Sparkles className="w-4 h-4 text-pink-500" />
          <span className="text-sm font-medium text-pink-600">Upcoming Event</span>
        </div>

        <h1 className="text-3xl sm:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-rose-500 mb-2">
          {title}
        </h1>

        {message && (
          <p className="text-muted-foreground max-w-md">{message}</p>
        )}
      </div>

      {/* Date & Time */}
      {startsAt && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
          <Calendar className="w-4 h-4" />
          <span>{format(new Date(startsAt), "EEEE, MMMM d")}</span>
          <span className="text-muted-foreground/50">•</span>
          <Clock className="w-4 h-4" />
          <span>{format(new Date(startsAt), "h:mm a")} EST</span>
        </div>
      )}

      {/* Countdown */}
      {countdown && (
        <div className="mb-8">
          <p className="text-sm text-muted-foreground mb-4 uppercase tracking-wide">
            Opens in
          </p>
          <div className="flex items-start justify-center gap-3 sm:gap-4">
            {countdown.days > 0 && (
              <>
                <TimeUnit value={countdown.days} label="Days" />
                <Separator />
              </>
            )}
            <TimeUnit value={countdown.hours} label="Hours" />
            <Separator />
            <TimeUnit value={countdown.minutes} label="Mins" />
            <Separator />
            <TimeUnit value={countdown.seconds} label="Secs" />
          </div>
        </div>
      )}

      {/* Reminder button */}
      <Button
        variant="outline"
        className="gap-2 rounded-full px-6"
        onClick={() => {
          // Could integrate with notification preferences
        }}
      >
        <Clock className="w-4 h-4" />
        Set Reminder
      </Button>

      {/* Info */}
      <div className="mt-8 p-4 rounded-2xl bg-muted/50 max-w-sm">
        <div className="flex items-start gap-3 text-sm text-muted-foreground">
          <Globe className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div className="text-left">
            <p className="font-medium text-foreground mb-1">What is Love Exhibition?</p>
            <p>
              A global live event where you can explore countries, join stages, 
              and meet people from around the world. Every Saturday!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExhibitionPreEventGate;
